/**
 * Store Initial State Factory Functions
 *
 * Pure factory functions for creating initial state objects used by
 * Legend State observable stores. Extracted for testability — these
 * functions define the "reset contracts" that resetPageUI() and
 * resetReadingUI() must satisfy.
 *
 * Each factory returns a fresh object to prevent shared-reference
 * mutation bugs between store instances or reset calls.
 */

import { Treatment } from "@bindery/shared/contracts/learning";
import type { ResponseId } from "@bindery/shared/contracts/fluency";
import type { ReadingSessionState } from "@/ear-reader/state/reading-session";
import type { UiState } from "@/ear-reader/state/ui-state";
import type { ExerciseState } from "@/ear-reader/state/exercise";
import type { VocabularyExerciseState } from "@/ear-reader/types/vocabulary-exercise";

// Reading & UI Stores

/**
 * Factory function for reading session initial state.
 * Returns a fresh object to prevent shared-reference mutation bugs.
 */
export function createInitialReadingSessionState(): ReadingSessionState {
  return {
    readingId: undefined,
    readingStartTime: undefined,
    lastEngagementIndex: undefined,
    lastEngagementTime: undefined,
    currentPage: undefined,
    pagesCompletedInSession: undefined,
    pageStartIndex: undefined,
    pageStartTime: undefined,
    wordsPerMinute: undefined,
    wordsReadThisSession: undefined,
    wordsReadLifetime: undefined,
    focusIndex: undefined,
    currentWordObj: undefined,
    currentWord: undefined,
  };
}

/**
 * Factory function for UI state initial values.
 * Returns a fresh object to prevent shared-reference mutation bugs.
 */
export function createInitialUiState(): UiState {
  return {
    isHighlighted: false,
    isDrawerOpen: false,
    activeWordLabel: undefined,
    showConfetti: false,
    isPlaying: false,
    inputType: "touch",
    nextPageAvailable: true,
    showCompletion: false,
    tutorialDismissed: false,
    navBarsVisible: true,
    flyMarkerTrigger: null,
  };
}

/**
 * Factory function for exercise state initial values.
 * Returns a fresh object to prevent shared-reference mutation bugs.
 */
export function createInitialExerciseState(): ExerciseState {
  return {
    currentTreatment: Treatment.DrawerMatch,
    controlTreatment: Treatment.AidedReading,
    precedingFluencyRecord: [],
    choices: null,
    choiceIndex: null,
    targetWordObj: null,
    incorrectMatches: null,
  };
}

/**
 * Factory function for vocabulary exercise initial state.
 * Returns a fresh object to prevent shared-reference mutation bugs.
 */
export function createInitialVocabularyExerciseState(): VocabularyExerciseState {
  return {
    focusMode: "fluency",
    reviewIndex: null,
    activeIndex: 0,
    wordList: [],
  };
}

export interface FluencyState {
  /** Keyed by normalized word. Replace the Map on write: Legend State does not see in-place mutation. */
  fluencyCache: Map<string, ResponseId[]>;
}

export function createInitialFluencyState(): FluencyState {
  return { fluencyCache: new Map<string, ResponseId[]>() };
}

// Reset Contract Functions

/**
 * Computes the UI state after a page-turn reset.
 *
 * Pure function that applies the resetPageUI contract:
 * - Clears engagement state (drawer, highlight, alerts, confetti, audio)
 * - Preserves user preferences (inputType, tutorialDismissed, etc.)
 *
 * This is the testable core of resetPageUI() from stores.ts.
 */
export function applyPageUIReset(currentUiState: UiState): UiState {
  return {
    ...currentUiState,
    isDrawerOpen: false,
    isHighlighted: false,
    activeWordLabel: undefined,
    showConfetti: false,
    isPlaying: false,
    flyMarkerTrigger: null,
  };
}

/**
 * Computes the exercise state after a page-turn reset.
 *
 * Pure function that applies the resetPageUI contract for exercises:
 * - Clears target word, choices, and incorrect matches
 * - Preserves treatment configuration and fluency record
 */
export function applyPageExerciseReset(
  currentExerciseState: ExerciseState,
): ExerciseState {
  return {
    ...currentExerciseState,
    targetWordObj: null,
    choices: null,
    choiceIndex: null,
    incorrectMatches: null,
  };
}
